import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useNotifications } from "@/context/NotificationsContext";
import { Button } from "@/components/ui/button";
import { ShoppingBag, User, Languages, LogOut, UtensilsCrossed, Bell } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { t, lang, toggleLang } = useLang();
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const { unread } = useNotifications();
  const navigate = useNavigate();
  const isStaff = !!(user && (user.is_driver || user.is_restaurant_owner || user.is_admin));
  const homePath = user?.is_driver ? "/driver" : user?.is_restaurant_owner ? "/owner" : user?.is_admin ? "/admin" : "/";

  return (
    <header className="glass-header z-50 flex-shrink-0" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center gap-3 md:gap-6">
        <Link to={homePath} className="flex items-center gap-2 min-w-0" data-testid="header-logo-link">
          <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-[#E05D3A] flex items-center justify-center text-white flex-shrink-0">
            <UtensilsCrossed className="w-4 h-4 md:w-5 md:h-5" />
          </div>
          <span className="font-display text-xl md:text-2xl font-black text-[#1A1A1A]">{t("brand")}</span>
        </Link>

        {!isStaff && (
          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-[#1A1A1A] ms-4">
            <Link to="/" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-home">{t("brand")}</Link>
            <Link to="/restaurants" className="hover:text-[#E05D3A] transition-colors" data-testid="nav-restaurants">
              {t("restaurants")}
            </Link>
          </nav>
        )}

        <div className="flex-1" />

        <button
          onClick={toggleLang}
          className="flex items-center gap-1.5 text-xs md:text-sm font-medium text-[#1A1A1A] hover:text-[#E05D3A] transition-colors whitespace-nowrap"
          data-testid="lang-toggle"
          aria-label="Toggle language"
        >
          <Languages className="w-4 h-4" />
          <span className="hidden sm:inline">{lang === "ar" ? "English" : "العربية"}</span>
          <span className="sm:hidden">{lang === "ar" ? "EN" : "ع"}</span>
        </button>

        {/* Desktop cart; mobile cart lives in bottom nav. Hidden for staff accounts. */}
        {!isStaff && (
          <Link to="/cart" className="relative hidden md:block" data-testid="header-cart-link">
            <Button variant="ghost" size="icon" className="rounded-full">
              <ShoppingBag className="w-5 h-5" />
            </Button>
            {itemCount > 0 && (
              <span className="absolute -top-1 -end-1 bg-[#E05D3A] text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-semibold">
                {itemCount}
              </span>
            )}
          </Link>
        )}

        {user ? (
          <>
            <Link to="/notifications" className="relative" data-testid="header-notifications-link" aria-label="Notifications">
              <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 md:h-10 md:w-10">
                <Bell className="w-5 h-5" />
              </Button>
              {unread > 0 && (
                <span className="absolute -top-0.5 -end-0.5 bg-[#E05D3A] text-white text-[10px] min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-bold" data-testid="notifications-badge">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </Link>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 md:h-10 md:w-10" data-testid="user-menu-trigger">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#00A85A] text-white flex items-center justify-center font-semibold text-sm">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-3 py-2">
                <p className="font-semibold text-sm truncate">{user.name}</p>
                <p className="text-xs text-[#6B6B6B] truncate">{user.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile">
                <User className="w-4 h-4 me-2" /> {t("profile")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-orders">
                <ShoppingBag className="w-4 h-4 me-2" /> {t("myOrders")}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                <LogOut className="w-4 h-4 me-2" /> {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </>
        ) : (
          <Button
            onClick={() => navigate("/login")}
            size="sm"
            className="rounded-full bg-[#E05D3A] hover:bg-[#C24B2C] text-white px-4 md:px-6 text-xs md:text-sm"
            data-testid="header-signin-btn"
          >
            {t("signIn")}
          </Button>
        )}
      </div>
    </header>
  );
}
